import React, { useEffect, useRef, useState } from "react";
import commoncss from "../../css/common.module.css";

const isNumeric = (val) => {
  if (val === "" || val === null || val === undefined) return false;
  if (typeof val === "string" && val.trim() === "") return false;
  const stripped = String(val).replace(/,/g, "").trim();
  if (stripped === "") return false;
  return !isNaN(Number(stripped));
};

export default function PreviewTemplateTableGenerator({
  value,
  onChange,
  forceOpen = false,
  onAfterOpen,
  selCat,
}) {
  const totalRows = 10;
  const totalCols = 10;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hoverRows, setHoverRows] = useState(0);
  const [hoverCols, setHoverCols] = useState(0);
  const [selectedRows, setSelectedRows] = useState(0);
  const [selectedCols, setSelectedCols] = useState(0);

  const [tableName, setTableName] = useState(value?.tableTitle || "");
  const [generated, setGenerated] = useState(false);
  const [cellValues, setCellValues] = useState({});
  const [exported, setExported] = useState(null);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  useEffect(() => {
    if (forceOpen) {
      openModal();
      onAfterOpen?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceOpen]);

  const initCells = (rows, cols) => {
    const next = {};
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) next[`${r}-${c}`] = "";
    }
    return next;
  };

  // ===== Prefill (TOP-LEFT IS COLUMN 1) =====
  useEffect(() => {
    if (!value) return;

    // rows: 1 header row + N data rows (labels live in col 0, rows 1..N)
    const rows = (value.tableRow?.length || 0) + 1;

    // cols: EXACTLY number of column headers (top-left is first header)
    const cols = value.tableColumn?.length || 0;

    if (rows <= 1 || cols <= 0) {
      // not configured yet
      setGenerated(false);
      setTableName(value.tableTitle || "");
      setSelectedRows(0);
      setSelectedCols(0);
      setCellValues({});
      return;
    }

    const base = initCells(rows, cols);

    // top headers (row 0) -> include top-left at 0-0
    value.tableColumn.forEach((c, i) => {
      base[`0-${i}`] = c?.name || "";
    });

    // left labels (col 0) start at row 1
    value.tableRow.forEach((r, i) => {
      base[`${i + 1}-0`] = r?.name || "";
      value.tableColumn.forEach((col, colIdx) => {
        if (colIdx > 0) {
          const colName = col?.name;
          if (colName && r[colName] !== undefined) {
            base[`${i + 1}-${colIdx}`] = r[colName];
          }
        }
      });
    });

    setSelectedRows(rows);
    setSelectedCols(cols);
    setCellValues(base);
    setTableName(value.tableTitle || "");
    setGenerated(true);
  }, [value]);

  // size picked from modal
  const handleModalUpdate = () => {
    if (hoverRows > 0 && hoverCols > 0) {
      setSelectedRows(hoverRows);
      setSelectedCols(hoverCols);
      setCellValues(initCells(hoverRows, hoverCols));
      setGenerated(true);
      closeModal();
    }
  };

  const handleCellChange = (r, c, v) =>
    setCellValues((prev) => ({ ...prev, [`${r}-${c}`]: v }));

  // ===== Export (TOP-LEFT INCLUDED AS COLUMN 1) =====
  const buildStructuredOutput = () => {
    const tableColumn = [];
    // include 0,0 as first column header
    for (let c = 0; c < selectedCols; c++) {
      const name = (cellValues[`0-${c}`] || "").trim();
      if (name) tableColumn.push({ name, lineType: "line item" });
    }

    const tableRow = [];
    // row labels live in col 0 for rows 1..selectedRows-1
    for (let r = 1; r < selectedRows; r++) {
      const name = (cellValues[`${r}-0`] || "").trim();
      if (name) tableRow.push({ name, lineType: "line item" });
    }

    const payload = {
      tableTitle: tableName || "Untitled Table",
      tableRow,
      tableColumn,
    };

    setExported(payload);
    onChange?.(payload);
  };

  const isNumericColumn = (colIdx) => {
    if (colIdx === 0) return false;
    // 1. If it's a standard 3-column financial table, col 1 and 2 are always values
    if (selectedCols === 3 && (colIdx === 1 || colIdx === 2)) return true;

    // 2. Iterate through rows to detect if it's a value column
    for (let r = 1; r < selectedRows; r++) {
      const val = String(cellValues[`${r}-${colIdx}`] || "").trim();
      if (!val || val === "-" || val === "0") continue;

      // If it's not numeric and not a common placeholder, it's a text column
      if (!isNumeric(val) && !/^\([\d,]+\.?\d*\)$/.test(val)) {
        return false;
      }
    }

    // 3. If no text found, treat as a value column (right-aligned)
    return true;
  };

  const modalRef = useRef(null);
  useEffect(() => {
    if (isModalOpen && modalRef.current) {
      modalRef.current.querySelector("input[name='tableName']")?.focus();
    }
  }, [isModalOpen]);

  const normalize = (str) =>
    String(str || "")
      .replace(/[^a-z0-9]/gi, "")
      .toLowerCase();
  const tableId = normalize(tableName);

  return (
    <div
      id={tableId}
      style={{
        margin: "8px 0",
        background: "#fff",
        border: "1px solid #eaeeed",
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      {tableName && (
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 10,
          alignItems: "center",
          background: "#fff",
          borderBottom: "1px solid #eaeeed",
          padding: "10px 12px",
        }}>
          <h5 style={{ margin: 0, color: "#205a46", fontSize: "15px", fontWeight: 600 }}>
            {tableName.toUpperCase()}
          </h5>
        </div>
      )}

      {generated && (
          <div
            className={commoncss.hideScroll}
            style={{
              overflow: "auto",
              maxHeight: "600px",
            }}
          >

            <table
              style={{
                borderCollapse: "collapse",
                width: "100%",
                minWidth: "max-content",
              }}
            >
              <tbody>
                {[...Array(selectedRows)].map((_, r) => {
                  const normalize = (str) => String(str || "").replace(/[^a-z0-9]/gi, "").toLowerCase();
                  const rowLabel = cellValues[`${r}-0`] || "";
                  const rowId = r > 0 ? normalize(rowLabel) : null;

                  return (
                    <tr key={r} id={rowId}>
                    {[...Array(selectedCols)].map((_, c) => {
                      // editable header row (r=0) and first column (c=0)
                      const editable = r === 0 || c === 0;
                      const key = `${r}-${c}`;
                      return (
                        <td
                          key={c}
                          style={{
                           border: "none",
                           padding: "10px 12px",
                           paddingRight: (selCat === "socie" && c > 0) ? "10px" : "12px",
                           verticalAlign: "top",

                            backgroundColor: r === 0 ? "#f4faf5" : "#fff",
                            fontWeight: r === 0 ? 600 : 400,
                            color: r === 0 ? "#205a46" : "#333",

                            textAlign: c === 0
                              ? "left"
                              : (selCat === "socie"
                                  ? (r === 0 ? "left" : "right")
                                  : (isNumericColumn(c) ? "right" : "left")
                                ),

                            width: c === 0
                              ? "300px"
                              : (selCat === "socie" ? "160px" : "220px"),
                            minWidth: c === 0
                              ? "280px"
                              : (selCat === "socie" ? "180px" : "200px"),

                            whiteSpace: r === 0 || c === 0 ? "normal" : "nowrap",
                            wordBreak: r === 0 || c === 0 ? "break-word" : "normal",
                          }}
                        >
                          {editable ? (
                           <div
                            style={{
                              width: "100%",
                              fontSize: 15,
                              fontWeight: r === 0 ? 600 : 400,
                              color: r === 0 ? "#205a46" : "#333",
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                              lineHeight: "18px",

                              wordBreak: "break-word",
                            }}
                          >
                              {r === 0
                                ? (cellValues[key] || "")
                                    .toLowerCase()
                                    .replace(/\b\w/g, (char) => char.toUpperCase())
                                : cellValues[key] ?? ""}
                            </div>
                          ) : (
                            <span>{cellValues[key] || 0 }</span>
                          )}
                        </td>
                      );
                    })}
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {exported && (
              <pre
                style={{
                  marginTop: 12,
                  padding: 12,
                  background: "#fafafa",
                  border: "1px solid #eee",
                  borderRadius: 8,
                  overflowX: "auto",
                  fontSize: 12,
                }}
              >
{JSON.stringify(exported, null, 2)}
              </pre>
            )}
          </div>
      )}

      {/* Config Modal */}
      {isModalOpen && (
        <div
          aria-modal="true"
          role="dialog"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 99,
            padding: 16,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsModalOpen(false);
          }}
        >
          <div
            ref={modalRef}
            style={{
              width: "min(720px, 96vw)",
              background: "#fff",
              borderRadius: 12,
              boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
              padding: 20,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <h3 style={{ margin: 0 }}>Configure Table</h3>
              <button
                onClick={() => setIsModalOpen(false)}
                aria-label="Close modal"
                style={{
                  background: "transparent",
                  border: "none",
                  fontSize: 22,
                  cursor: "pointer",
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>

            <label htmlFor="tableName" style={{ display: "block", marginBottom: 6 }}>
              Table Name
            </label>
            <input
              id="tableName"
              name="tableName"
              type="text"
              value={tableName}
              onChange={(e) => setTableName(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid #d0d0d0",
                marginBottom: 16,
                backgroundColor: "#f8fff8",
              }}
              disabled
            />

            <div style={{ marginBottom: 6 }}>Table Layout (Rows × Columns)</div>
            <SizePicker
              totalRows={totalRows}
              totalCols={totalCols}
              hoverRows={hoverRows}
              hoverCols={hoverCols}
              selectedRows={selectedRows}
              selectedCols={selectedCols}
              onHover={(r, c) => {
                setHoverRows(r + 1);
                setHoverCols(c + 1);
              }}
              onPick={() => {
                if (hoverRows > 0 && hoverCols > 0) {
                  setSelectedRows(hoverRows);
                  setSelectedCols(hoverCols);
                }
              }}
            />

            <div style={{ marginTop: 8, fontWeight: 500, color: "#333" }}>
              {(selectedRows || hoverRows) > 0 && (selectedCols || hoverCols) > 0
                ? `${selectedRows || hoverRows} Rows × ${selectedCols || hoverCols} Columns`
                : "Select table size"}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
              <button
                onClick={() => setIsModalOpen(false)}
                style={{
                  backgroundColor: "#eaeaea",
                  border: "none",
                  borderRadius: 10,
                  padding: "10px 16px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleModalUpdate}
                style={{
                  backgroundColor: "#205a46",
                  color: "#fff",
                  border: "none",
                  borderRadius: 10,
                  padding: "10px 16px",
                  cursor: "pointer",
                }}
                disabled={!hoverRows || !hoverCols}
                title={!hoverRows || !hoverCols ? "Pick a size first" : "Create table"}
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SizePicker({
  totalRows,
  totalCols,
  hoverRows,
  hoverCols,
  selectedRows,
  selectedCols,
  onHover,
  onPick,
}) {
  return (
    <div
      style={{
        display: "inline-block",
        padding: 10,
        backgroundColor: "#fafafa",
        borderRadius: 10,
        border: "1px solid #e3e3e3",
      }}
    >
      {[...Array(totalRows)].map((_, r) => (
        <div key={r} style={{ display: "flex" }}>
          {[...Array(totalCols)].map((_, c) => {
            const isActive =
              r < (selectedRows || hoverRows) && c < (selectedCols || hoverCols);
            return (
              <div
                key={c}
                onMouseEnter={() => onHover(r, c)}
                onClick={onPick}
                style={{
                  width: 22,
                  height: 22,
                  margin: 2,
                  border: "1px solid #ccc",
                  backgroundColor: isActive ? "#b6e8c4" : "#fff",
                  cursor: "pointer",
                  transition: "background-color 0.15s ease-in-out",
                }}
                title={`${r + 1} × ${c + 1}`}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}